/**
 * @fileoverview Connect explicit task ownership changes to backend-selected eligible assignees. The caller supplies current task identity; the backend owns accepted take/assign transitions and visibility. Assignment selection and accepting a domain review action remain distinct responsibilities.
 */

import React, { useEffect, useRef, useState } from 'react';
import api from '../../api';
import AppIcon from '../shared/AppIcon';
import AppModal from '../shared/AppModal';



export default function TaskAssignment({ task, actor, onTaskChanged }) {
  const [open, setOpen] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const requestRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; requestRef.current += 1; };
  }, []);
  const owner = task.taskOwner;
  const held = task.paymentReview?.status === 'held';

  const loadAssignees = 

  async () => {
    const requestId = ++requestRef.current;
    setOpen(true);
    setLoading(true);
    setError('');
    setSelected(owner?.employee_id ? String(owner.employee_id) : '');
    try {
      const response = await api.get(`/tasks/${task.id}/assignees`);
      if (requestId !== requestRef.current) return;
      const options = response.data?.assignees || [];
      setAssignees(options);
      const current = options.find((person) => owner?.employee_id
        ? person.employee_id === owner.employee_id
        : owner?.email && person.email?.toLowerCase() === owner.email.toLowerCase());
      setSelected(current ? String(current.employee_id) : owner ? 'unavailable' : '');
    } catch {
      if (requestId === requestRef.current) setError('Unable to load eligible assignees.');
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  };

  const save = 

  async (take = false) => {
    setBusy(true);
    setError('');
    try {
      if (take) await api.post(`/tasks/${task.id}/take`);
      else await api.post(`/tasks/${task.id}/assign`, { employee_id: selected ? Number(selected) : null });
      if (!mountedRef.current) return;
      setOpen(false);
      await onTaskChanged(task.id);
    } catch (failure) {
      if (mountedRef.current) setError(failure?.response?.data?.error || 'Unable to update assignment. Try again.');
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  return (
    <section className="task-inbox__assignment" aria-label="Task assignment">
      <span><AppIcon icon="pi pi-user" aria-hidden="true" /> {owner?.name || owner?.email || 'Unassigned'}</span>
      {task.status === 'open' ? <div className="task-inbox__assignment-actions">
        {!owner && actor?.employee_id ? <button type="button" className="task-inbox__button" disabled={busy} onClick={() => save(true)}>Take task</button> : null}
        <button type="button" className="task-inbox__button" disabled={busy} onClick={loadAssignees}>Assign</button>
      </div> : null}
      {error && !open ? <p role="alert">{error}</p> : null}
      <AppModal visible={open} onClose={() => { if (!busy) { setOpen(false); requestRef.current += 1; } }} title="Assign task"
        footer={<button type="button" className="task-inbox__button task-inbox__button--primary"
          disabled={busy || loading || !!error || selected === 'unavailable' || (held && !selected)} onClick={() => save()}>
          {busy ? 'Saving…' : 'Save assignment'}
        </button>}>
        {loading ? <p role="status">Loading eligible assignees…</p> : error ? <div role="alert"><p>{error}</p>
          <button type="button" className="task-inbox__button" onClick={loadAssignees}>Try again</button></div> :
          <label className="task-inbox__assignee-field">Assign to
            <select value={selected} disabled={busy} onChange={(event) => setSelected(event.target.value)}>
              <option value="" disabled={held}>Unassigned</option>
              {selected === 'unavailable' ? <option value="unavailable" disabled>Current owner unavailable</option> : null}
              {assignees.map((person) => <option key={person.employee_id} value={person.employee_id}>{person.name || person.email}</option>)}
            </select>
          </label>}
      </AppModal>
    </section>
  );
}
